import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetScrollView,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import {
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { useSheet } from "../shared/sheet-provider";
import CloseIcon from "@/assets/svgs/close-icon";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import Markdown from "react-native-markdown-display";
import { Button } from "../ui/button";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

const markdownStyles = {
  body: { fontFamily: "Geologica_400Regular", color: "#181718" },
  heading1: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading2: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading3: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading4: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading5: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading6: { fontFamily: "Geologica_500Medium", color: "#181718" },
  text: { color: "#181718" },
  strong: { fontFamily: "Geologica_500Medium", color: "#181718" },
};

function LegalSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet(SheetIds.LEGAL);
  const { payload } = sheet ?? {};
  const title = payload?.title;
  const content = payload?.content;
  const status = payload?.status ?? "pending";
  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;
  const scrollViewRef = useRef<ScrollView | null>(null);

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        scrollViewRef.current?.scrollTo({ y: 0, animated: true });
        dismiss();
      }
    },
    [dismiss]
  );

  const onClose = useCallback(() => {
    close({
      status,
    });
  }, [close, status]);

  const onAccept = useCallback(() => {
    close({
      status: "accepted",
    });
  }, [close]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      containerStyle={{ width: responsiveWidth, marginLeft: marginLeft }}
      snapPoints={["100%"]}
    >
      <BottomSheetScrollView ref={scrollViewRef} stickyHeaderIndices={[0]}>
        <View className="flex-1 bg-teachLoop-creamBase rounded-t-[20]">
          <View className="h-6" />
          <View className="mx-5 items-end">
            <Pressable onPress={onClose} className="active:opacity-80">
              <CloseIcon
                className="size-6"
                pathClassName="stroke-teachLoop-textPrimary"
              />
            </Pressable>
          </View>
          <View className="h-6" />
        </View>

        <View className="mx-5">
          <Text className="text-xl font-geologica-medium font-medium text-black">
            {title}
          </Text>
          <View className="h-4" />
          <Markdown style={markdownStyles}>{content}</Markdown>
        </View>
      </BottomSheetScrollView>

      <View className="m-5">
        <Button
          className="w-full"
          size="md"
          intent="primary"
          onPress={onAccept}
        >
          <Button.Text size="md">I agree</Button.Text>
        </Button>
      </View>
    </BottomSheet>
  ) : null;
}

export default LegalSheet;
